import { useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { Save, Trash2, Plus, RefreshCcw, AlertTriangle } from 'lucide-react'
import { adminApi, type AdminInterviewerOption } from '../../lib/api/admin'
import type {
  SchedulerConfig,
  WeeklyRule,
  DateOverride,
} from '../../lib/api/scheduler'
import { friendlyApiError } from '../../lib/utils/apiError'

// Self-contained admin editor for ONE interviewer's native scheduler (feature #9).
// It intentionally does NOT reuse the (large) InterviewerSchedule.tsx internals —
// duplicating a little presentational code here keeps the interviewer page free of
// regression risk. All reads/writes go through the admin API (which is keyed on
// interviewer_profiles.id, i.e. AdminInterviewerOption.interviewer_id), NOT the
// interviewer-facing /scheduler/* endpoints. Times are labelled in the
// interviewer's own timezone (config.timezone).

const FULL_DAY_LABELS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]
const BUFFER_OPTIONS = [0, 30, 60, 120] as const

interface Window {
  start: string
  end: string
}

export default function AdminManageAvailability({
  interviewers,
}: {
  interviewers: AdminInterviewerOption[]
}) {
  const [selectedId, setSelectedId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [config, setConfig] = useState<SchedulerConfig | null>(null)
  const [windowsByDay, setWindowsByDay] = useState<Window[][]>(() =>
    Array.from({ length: 7 }, () => []),
  )
  const [overrides, setOverrides] = useState<DateOverride[]>([])

  const [savingConfig, setSavingConfig] = useState(false)
  const [savingRules, setSavingRules] = useState(false)
  const [addingOverride, setAddingOverride] = useState(false)

  // New-override form state.
  const [ovDate, setOvDate] = useState('')
  const [ovType, setOvType] = useState<'custom' | 'blocked'>('custom')
  const [ovStart, setOvStart] = useState('09:00')
  const [ovEnd, setOvEnd] = useState('17:00')

  const tz = config?.timezone || 'UTC'

  const loadAll = useCallback(async (interviewerId: number) => {
    setLoading(true)
    try {
      const [cfg, rules, ovs] = await Promise.all([
        adminApi.getInterviewerConfig(interviewerId),
        adminApi.getInterviewerRules(interviewerId),
        adminApi.getInterviewerOverrides(interviewerId),
      ])
      setConfig(cfg)
      const grouped: Window[][] = Array.from({ length: 7 }, () => [])
      rules
        .filter((r) => r.is_active)
        .forEach((r) => {
          if (r.day_of_week >= 0 && r.day_of_week <= 6)
            grouped[r.day_of_week].push({ start: r.start_time, end: r.end_time })
        })
      grouped.forEach((w) => w.sort((a, b) => a.start.localeCompare(b.start)))
      setWindowsByDay(grouped)
      setOverrides(ovs)
    } catch (err) {
      toast.error(friendlyApiError(err))
      setConfig(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedId) loadAll(Number(selectedId))
    else {
      setConfig(null)
      setWindowsByDay(Array.from({ length: 7 }, () => []))
      setOverrides([])
    }
  }, [selectedId, loadAll])

  // ── Config edits (Part B) ──────────────────────────────────────────────────
  const patchConfig = (patch: Partial<SchedulerConfig>) =>
    setConfig((c) => (c ? { ...c, ...patch } : c))

  const handleSaveConfig = async () => {
    if (!config || !selectedId) return
    setSavingConfig(true)
    try {
      const saved = await adminApi.setInterviewerConfig(Number(selectedId), {
        timezone: config.timezone,
        buffer_before_minutes: config.buffer_before_minutes,
        buffer_after_minutes: config.buffer_after_minutes,
        // Min notice is no longer editable here, but it is still sent back
        // exactly as loaded so saving these settings can't reset the
        // interviewer's stored value.
        min_notice_hours: config.min_notice_hours,
        booking_window_days: config.booking_window_days,
        // Accepting-bookings and admin-lock are no longer editable here either,
        // so echo them back untouched — saving buffers must not flip an
        // interviewer's accepting toggle or lock state.
        is_accepting_bookings: config.is_accepting_bookings,
        admin_locked: config.admin_locked,
      })
      setConfig(saved)
      toast.success('Scheduler settings saved')
    } catch (err) {
      toast.error(friendlyApiError(err))
    } finally {
      setSavingConfig(false)
    }
  }

  // ── Weekly rule edits ───────────────────────────────────────────────────────
  const addWindow = (day: number) =>
    setWindowsByDay((prev) => {
      const next = prev.map((w) => [...w])
      next[day].push({ start: '09:00', end: '17:00' })
      return next
    })

  const removeWindow = (day: number, idx: number) =>
    setWindowsByDay((prev) => {
      const next = prev.map((w) => [...w])
      next[day].splice(idx, 1)
      return next
    })

  const updateWindow = (day: number, idx: number, field: 'start' | 'end', value: string) =>
    setWindowsByDay((prev) => {
      const next = prev.map((w) => [...w])
      next[day][idx] = { ...next[day][idx], [field]: value }
      return next
    })

  const handleSaveRules = async () => {
    if (!selectedId) return
    // Client-side sanity: each window must have end > start.
    for (let day = 0; day < 7; day++) {
      for (const w of windowsByDay[day]) {
        if (!w.start || !w.end || w.end <= w.start) {
          toast.error(`${FULL_DAY_LABELS[day]}: end time must be after start time.`)
          return
        }
      }
    }
    const rules: WeeklyRule[] = []
    windowsByDay.forEach((windows, day) => {
      windows.forEach((w) =>
        rules.push({ day_of_week: day, start_time: w.start, end_time: w.end, is_active: true }),
      )
    })
    setSavingRules(true)
    try {
      const res = await adminApi.setInterviewerRules(Number(selectedId), rules)
      if (res.warning) toast(res.warning, { icon: '⚠️', duration: 8000 })
      else toast.success('Weekly availability saved')
      await loadAll(Number(selectedId))
    } catch (err) {
      toast.error(friendlyApiError(err))
    } finally {
      setSavingRules(false)
    }
  }

  // ── Date override edits ──────────────────────────────────────────────────────
  const handleAddOverride = async () => {
    if (!selectedId || !ovDate) {
      toast.error('Pick a date for the override.')
      return
    }
    const body =
      ovType === 'custom'
        ? { date: ovDate, type: 'custom' as const, start_time: ovStart, end_time: ovEnd }
        : { date: ovDate, type: 'blocked' as const }
    setAddingOverride(true)
    try {
      const res = await adminApi.createInterviewerOverride(Number(selectedId), body)
      if (res.warning) toast(res.warning, { icon: '⚠️', duration: 8000 })
      else toast.success('Override added')
      setOvDate('')
      await loadAll(Number(selectedId))
    } catch (err) {
      toast.error(friendlyApiError(err))
    } finally {
      setAddingOverride(false)
    }
  }

  const handleDeleteOverride = async (id: number) => {
    if (!selectedId) return
    try {
      const res = await adminApi.deleteInterviewerOverride(Number(selectedId), id)
      if (res.warning) toast(res.warning, { icon: '⚠️', duration: 8000 })
      else toast.success('Override removed')
      await loadAll(Number(selectedId))
    } catch (err) {
      toast.error(friendlyApiError(err))
    }
  }

  return (
    <div className="card">
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Manage Interviewer Availability</h3>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          View and edit a specific interviewer&apos;s weekly hours, date overrides, and
          booking rules on their behalf. Changes never cancel existing bookings.
        </p>
      </div>

      {/* Interviewer picker (keyed on interviewer_profiles.id) */}
      <div className="form-group" style={{ maxWidth: 420 }}>
        <label className="form-label">Interviewer</label>
        <select
          className="form-input"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          <option value="">Select an interviewer...</option>
          {interviewers.map((iv) => (
            <option key={iv.interviewer_id} value={String(iv.interviewer_id)}>
              {iv.alias || `Interviewer #${iv.interviewer_id}`}
            </option>
          ))}
        </select>
      </div>

      {!selectedId ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: '20px 0' }}>
          Choose an interviewer above to view and edit their scheduler.
        </p>
      ) : loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: '20px 0' }}>
          Loading scheduler…
        </p>
      ) : !config ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '20px 0', color: 'var(--danger)' }}>
          <RefreshCcw size={16} />
          <button className="btn btn-secondary" onClick={() => loadAll(Number(selectedId))}>
            Retry loading
          </button>
        </div>
      ) : (
        <>
          {/* ── Part B: booking config ─────────────────────────────────────── */}
          <section style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 15 }}>Booking settings</h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 16,
              }}
            >
              <div className="form-group">
                <label className="form-label">Timezone (IANA)</label>
                <input
                  className="form-input"
                  value={config.timezone}
                  onChange={(e) => patchConfig({ timezone: e.target.value })}
                  placeholder="America/New_York"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Booking window (days, 1–45)</label>
                <input
                  type="number"
                  className="form-input"
                  min={1}
                  max={45}
                  value={config.booking_window_days}
                  onChange={(e) =>
                    patchConfig({ booking_window_days: Number(e.target.value) })
                  }
                />
                {/* The field above is the STANDARD window. When an extension is
                    approved the interviewer's own page shows the larger effective
                    number, so show it here too — otherwise admin reads "45" while
                    the interviewer reads "48 days" for the same person. */}
                {config.extra_days && config.extra_days > 0 ? (
                  <p style={{ fontSize: 12, marginTop: 6 }}>
                    <strong>
                      Effective: {config.booking_window_days} + {config.extra_days} approved
                      extra = {config.effective_window_days} days
                    </strong>
                    {config.effective_until ? ` (through ${config.effective_until} today)` : ''}
                    .{' '}
                    <span style={{ color: 'var(--text-muted)' }}>
                      Clients can book that far out. Manage it under Availability
                      Extensions.
                    </span>
                  </p>
                ) : (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    45 is the standard maximum. To take an interviewer beyond it (up to
                    60 days), approve an availability extension instead.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Buffer before</label>
                <select
                  className="form-input"
                  value={config.buffer_before_minutes}
                  onChange={(e) =>
                    patchConfig({ buffer_before_minutes: Number(e.target.value) })
                  }
                >
                  {BUFFER_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m === 0 ? 'No buffer' : `${m} min`}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Buffer after</label>
                <select
                  className="form-input"
                  value={config.buffer_after_minutes}
                  onChange={(e) =>
                    patchConfig({ buffer_after_minutes: Number(e.target.value) })
                  }
                >
                  {BUFFER_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m === 0 ? 'No buffer' : `${m} min`}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={handleSaveConfig}
              disabled={savingConfig}
            >
              <Save size={16} /> {savingConfig ? 'Saving…' : 'Save booking settings'}
            </button>
          </section>

          {/* ── Part A: weekly rules ───────────────────────────────────────── */}
          <section style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ margin: '0 0 4px', fontSize: 15 }}>Weekly hours</h4>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
              Times are in the interviewer&apos;s timezone ({tz}).
            </p>
            {FULL_DAY_LABELS.map((label, day) => (
              <div
                key={day}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom: '1px solid var(--border-light, #eee)',
                }}
              >
                <div style={{ width: 100, fontWeight: 600, fontSize: 14, paddingTop: 6 }}>{label}</div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {windowsByDay[day].length === 0 && (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)', paddingTop: 6 }}>Unavailable</span>
                  )}
                  {windowsByDay[day].map((w, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="time"
                        className="form-input"
                        style={{ width: 130 }}
                        value={w.start}
                        onChange={(e) => updateWindow(day, idx, 'start', e.target.value)}
                      />
                      <span>–</span>
                      <input
                        type="time"
                        className="form-input"
                        style={{ width: 130 }}
                        value={w.end}
                        onChange={(e) => updateWindow(day, idx, 'end', e.target.value)}
                      />
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 10px', color: 'var(--danger)' }}
                        onClick={() => removeWindow(day, idx)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '4px 10px', fontSize: 13, alignSelf: 'flex-start' }}
                    onClick={() => addWindow(day)}
                  >
                    <Plus size={14} /> Add window
                  </button>
                </div>
              </div>
            ))}
            <button
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={handleSaveRules}
              disabled={savingRules}
            >
              <Save size={16} /> {savingRules ? 'Saving…' : 'Save weekly hours'}
            </button>
          </section>

          {/* ── Part A: date overrides ─────────────────────────────────────── */}
          <section style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 15 }}>Date overrides</h4>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Date</label>
                <input type="date" className="form-input" value={ovDate} onChange={(e) => setOvDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Type</label>
                <select
                  className="form-input"
                  value={ovType}
                  onChange={(e) => setOvType(e.target.value as 'custom' | 'blocked')}
                >
                  <option value="custom">Custom hours</option>
                  <option value="blocked">Blocked (day off)</option>
                </select>
              </div>
              {ovType === 'custom' && (
                <>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Start</label>
                    <input type="time" className="form-input" style={{ width: 130 }} value={ovStart} onChange={(e) => setOvStart(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">End</label>
                    <input type="time" className="form-input" style={{ width: 130 }} value={ovEnd} onChange={(e) => setOvEnd(e.target.value)} />
                  </div>
                </>
              )}
              <button className="btn btn-primary" onClick={handleAddOverride} disabled={addingOverride}>
                <Plus size={16} /> {addingOverride ? 'Adding…' : 'Add override'}
              </button>
            </div>

            {overrides.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No date overrides.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Hours ({tz})</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((o) => (
                      <tr key={o.id}>
                        <td>{o.date}</td>
                        <td>
                          {o.type === 'blocked' ? (
                            <span className="badge badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <AlertTriangle size={12} /> Blocked
                            </span>
                          ) : (
                            <span className="badge badge-success">Custom</span>
                          )}
                        </td>
                        <td>{o.type === 'custom' ? `${o.start_time}–${o.end_time}` : '—'}</td>
                        <td>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 10px', color: 'var(--danger)' }}
                            onClick={() => handleDeleteOverride(o.id)}
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
